import React, { useRef, useEffect } from 'react';

const AttitudeIndicator = ({ mavTimestamp, selectedMavId, size = 300, showBearingMarkers = true }) => {
    const canvasRef = useRef(null);
    let pitch = 0;
    let roll = 0;
    let yaw = 0;
    let homeBearing;
    let waypointBearing;

    // Define color constants
    const SKY_COLOR = '#2383F9'; // Sky color
    const GROUND_COLOR = '#8FB14E'; // Ground color (QGC-style light green)
    const HORIZON_LINE_COLOR = '#FFFFFF'; // Horizon line color
    const PITCH_LINE_COLOR = '#FFFFFF'; // Pitch line color
    const ROLL_INDICATOR_COLOR = '#FFFFFF'; // Roll indicator color
    const LEVEL_INDICATOR_COLOR = '#FD9B2A'; // Level indicator color
    const HOME_INDICATOR_COLOR = '#00EE00';
    const WAYPOINT_INDICATOR_COLOR = '#FFFFFF';

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        const centerX = width / 2;
        const centerY = height / 2;
        if (mav[selectedMavId]) {
            const vehicle = mav[selectedMavId];
            if (vehicle.ATTITUDE !== undefined) {
                pitch = vehicle.ATTITUDE.pitch;
                roll = vehicle.ATTITUDE.roll;
                yaw = vehicle.ATTITUDE.yaw;
                if (vehicle.GPS_RAW_INT !== undefined) {
                    const vehicleLatitude = vehicle.GPS_RAW_INT.lat / 1e7;
                    const vehicleLongitude = vehicle.GPS_RAW_INT.lon / 1e7;
                    if (vehicle.HOME_POSITION !== undefined) {
                        const homeLatitude = vehicle.HOME_POSITION.latitude / 1e7;
                        const homeLongitude = vehicle.HOME_POSITION.longitude / 1e7;

                        if (homeLatitude !== undefined && homeLongitude !== undefined && vehicleLatitude !== undefined && vehicleLongitude !== undefined) {
                            homeBearing = (Math.atan2(homeLongitude - vehicleLongitude, homeLatitude - vehicleLatitude) * (180 / Math.PI) - yaw * (180 / Math.PI) + 360) % 360; // Adjust based on current yaw and ensure it's in the range 0-360
                        }
                    }

                    if (vehicle.POSITION_TARGET_GLOBAL_INT !== undefined) {
                        const targetLatitude = vehicle.POSITION_TARGET_GLOBAL_INT.lat_int / 1e7;
                        const targetLongitude = vehicle.POSITION_TARGET_GLOBAL_INT.lon_int / 1e7;

                        if (targetLatitude !== undefined && targetLongitude !== undefined && vehicleLatitude !== undefined && vehicleLongitude !== undefined) {
                            waypointBearing = (Math.atan2(targetLongitude - vehicleLongitude, targetLatitude - vehicleLatitude) * (180 / Math.PI) - yaw * (180 / Math.PI) + 360) % 360; // Adjust based on current yaw and ensure it's in the range 0-360
                        }
                    }
                }
            }
        }

        const drawAttitudeIndicator = () => {
            ctx.clearRect(0, 0, width, height);

            // Save the context state
            ctx.save();

            // Translate to the center of the canvas
            ctx.translate(centerX, centerY);

            // Rotate the canvas based on the roll value (negated to correct the direction)
            ctx.rotate(-roll);

            // Translate vertically based on the pitch value
            ctx.translate(0, (pitch * height) / Math.PI);

            // Draw the sky
            ctx.fillStyle = SKY_COLOR;
            ctx.fillRect(-centerX * 2, -centerY * 2, width * 2, height * 2);

            // Draw the ground
            ctx.fillStyle = GROUND_COLOR;
            ctx.fillRect(-centerX * 2, 0, width * 2, height * 2);

            // Draw the horizon line
            ctx.strokeStyle = HORIZON_LINE_COLOR;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(-centerX * 2, 0);
            ctx.lineTo(centerX * 2, 0);
            ctx.stroke();

            // Draw the pitch lines
            ctx.strokeStyle = PITCH_LINE_COLOR;
            ctx.lineWidth = 1;
            for (let i = -90; i <= 90; i += 30) {
                const y = (i * height) / 180;
                ctx.beginPath();
                ctx.moveTo(-centerX / 4, y);
                ctx.lineTo(centerX / 4, y);
                ctx.stroke();
            }

            // Restore the context state
            ctx.restore();

            // Draw the roll indicator
            ctx.strokeStyle = ROLL_INDICATOR_COLOR;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(centerX, centerY, centerX - 1, 0, 2 * Math.PI);
            ctx.stroke();

            // Draw the roll lines in 10-degree steps
            ctx.save();
            ctx.translate(centerX, centerY);
            for (let i = -180; i <= 180; i += 30) {
                ctx.rotate((i * Math.PI) / 180);
                ctx.beginPath();
                ctx.moveTo(0, -centerX + 1);
                ctx.lineTo(0, -centerX + 11);
                ctx.stroke();
                ctx.rotate((-i * Math.PI) / 180);
            }
            ctx.restore();

            // Draw the fixed level indicator
            ctx.strokeStyle = LEVEL_INDICATOR_COLOR;
            ctx.lineWidth = 2;

            // Draw the dot in the middle
            ctx.beginPath();
            ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
            ctx.fillStyle = LEVEL_INDICATOR_COLOR;
            ctx.fill();

            // Draw the lines on either side of the dot
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(centerX - 80, centerY);
            ctx.lineTo(centerX - 20, centerY);
            ctx.moveTo(centerX + 20, centerY);
            ctx.lineTo(centerX + 80, centerY);
            ctx.stroke();

            // Draw bearing markers (Home / Target) scaled to canvas size
            const drawBearingMarker = (bearing, color, label) => {
                const arrowHalfW = Math.max(4, size * 0.035);
                const arrowLen = Math.max(8, size * 0.07);
                const tipR = centerY - Math.max(3, size * 0.025);
                const baseR = tipR - arrowLen;
                const fontSize = Math.max(10, Math.round(size * 0.11));
                const labelR = baseR - fontSize * 0.55;

                ctx.save();
                ctx.translate(centerX, centerY);
                ctx.rotate((bearing - yaw) * (Math.PI / 180));

                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -tipR);
                ctx.lineTo(-arrowHalfW, -baseR);
                ctx.lineTo(arrowHalfW, -baseR);
                ctx.closePath();
                ctx.fill();

                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineWidth = Math.max(2, fontSize * 0.18);
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
                ctx.strokeText(label, 0, -labelR);
                ctx.fillStyle = color;
                ctx.fillText(label, 0, -labelR);

                ctx.restore();
            };

            if (showBearingMarkers && homeBearing) {
                drawBearingMarker(homeBearing, HOME_INDICATOR_COLOR, 'H');
            }

            if (showBearingMarkers && waypointBearing) {
                drawBearingMarker(waypointBearing, WAYPOINT_INDICATOR_COLOR, 'T');
            }


            // Draw the yaw value in degrees in the middle
            ctx.fillStyle = '#FFFFFF';
            ctx.font = '20px Arial';
            const yawDegrees = (yaw * 180 / Math.PI + 360) % 360; // Convert yaw to degrees and ensure it's in the range 0-360
            let yawDegreesPositionOffset = centerX;;
            if (yawDegrees < 10) {
                yawDegreesPositionOffset -= 5;
            } else if (yawDegrees < 100) {
                yawDegreesPositionOffset -= 12;
            } else {
                yawDegreesPositionOffset -= 18;
            }
            ctx.fillText(`${yawDegrees.toFixed(0)}°`, yawDegreesPositionOffset, centerY - 90);
        };

        drawAttitudeIndicator();
    }, [mavTimestamp]);

    return <canvas ref={canvasRef} width={size} height={size} style={{ borderRadius: '50%' }} />;
};

export default AttitudeIndicator;